import type { Database } from '@/integrations/supabase/types';
import type { RecipientToken } from '@/lib/admin/email-templates/constants/recipient-tokens';

/** Riga grezza della tabella `email_templates` (chiave primaria: `template_key`). */
export type EmailTemplateRow = Database['public']['Tables']['email_templates']['Row'];

/**
 * Un record pilota DUE canali distinti:
 * - `email`: `subject` + `recipients` + `mailgun_draft_link`
 * - `notification`: `notification_message` + `notification_recipients` + `redirect_url`
 */
export type EmailTemplateChannel = 'email' | 'notification';

/**
 * Modello normalizzato usato dalla UI: gli array nullable a DB diventano array
 * sempre valorizzati, così i componenti non devono difendersi dai null.
 */
export interface EmailTemplate {
  template_key: string;
  subject: string;
  description: string | null;
  recipients: string[];
  mailgun_draft_link: string | null;
  redirect_url: string | null;
  notification_recipients: string[];
  notification_message: string | null;
  available_variables: string[];
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Payload di scrittura: `created_at`/`updated_at` sono gestiti dal DB (default +
 * trigger) e non vengono mai inviati dal client.
 */
export type EmailTemplatePayload = Omit<EmailTemplate, 'created_at' | 'updated_at'>;

export const EmailTemplateFactory = {
  fromRow(row: EmailTemplateRow): EmailTemplate {
    return {
      template_key: row.template_key,
      subject: row.subject ?? '',
      description: row.description,
      recipients: row.recipients ?? [],
      mailgun_draft_link: row.mailgun_draft_link,
      redirect_url: row.redirect_url,
      notification_recipients: row.notification_recipients ?? [],
      notification_message: row.notification_message,
      available_variables: row.available_variables ?? [],
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  },

  /** Record vuoto per la modale in modalità creazione. */
  empty(): EmailTemplatePayload {
    return {
      template_key: '',
      subject: '',
      description: null,
      recipients: [],
      mailgun_draft_link: null,
      redirect_url: null,
      notification_recipients: [],
      notification_message: null,
      available_variables: [],
    };
  },

  /** Copia di un record esistente, con chiave suffissata perché la PK è unica. */
  duplicate(template: EmailTemplate): EmailTemplatePayload {
    return {
      template_key: `${template.template_key}_copy`,
      subject: template.subject,
      description: template.description,
      recipients: [...template.recipients],
      mailgun_draft_link: template.mailgun_draft_link,
      redirect_url: template.redirect_url,
      notification_recipients: [...template.notification_recipients],
      notification_message: template.notification_message,
      available_variables: [...template.available_variables],
    };
  },

  toPayload(template: EmailTemplate): EmailTemplatePayload {
    const { created_at, updated_at, ...payload } = template;
    void created_at;
    void updated_at;
    return payload;
  },
};

/** Destinatari del canale richiesto. */
export const getChannelRecipients = (
  template: Pick<EmailTemplatePayload, 'recipients' | 'notification_recipients'>,
  channel: EmailTemplateChannel
): string[] =>
  channel === 'email' ? template.recipients : template.notification_recipients;

/**
 * "Attivo" non è una colonna: un canale invia solo se il suo array di destinatari
 * non è vuoto (`recipients` vuoto ⇒ nessuna email, `notification_recipients` vuoto
 * ⇒ nessuna notifica). Lo stato in tabella è quindi derivato, non persistito.
 */
export const isChannelEnabled = (
  template: Pick<EmailTemplatePayload, 'recipients' | 'notification_recipients'>,
  channel: EmailTemplateChannel
): boolean => getChannelRecipients(template, channel).length > 0;

/** Placeholder con graffe SINGOLE, come atteso dal mailer: `{client_first_name}`. */
export const formatVariablePlaceholder = (variable: string): string => `{${variable}}`;

/** Type guard usata solo per le etichette i18n dei token noti. */
export const isKnownRecipientToken = (
  value: string,
  tokens: readonly string[]
): value is RecipientToken => tokens.includes(value);
